import styled from 'styled-components';
import type { AvatarSizes } from '@frameio/vapor';
import { MenuItem, theme } from '@frameio/vapor';
import { ProjectHeaderContent } from './ProjectHeaderContent';

export type ProjectHeaderMenuItemProps = {
  avatarSize?: AvatarSizes;
  avatarUrl: string;
  details: string;
  isPrivate?: boolean;
  projectId: string;
  projectName: string;
};

export function ProjectHeaderMenuItem({
  avatarSize,
  avatarUrl,
  details,
  isPrivate,
  projectId,
  projectName,
}: ProjectHeaderMenuItemProps) {
  return (
    <MenuItem>
      {({ bind }) => (
        <Container
          data-testid="project-header-menu-item"
          role="menuitem"
          {...bind}
        >
          <ProjectHeaderContent
            avatarSize={avatarSize}
            avatarUrl={avatarUrl}
            details={details}
            emphasis="prominent"
            isPrivate={isPrivate}
            projectId={projectId}
            projectName={projectName}
            truncateName={false}
          />
        </Container>
      )}
    </MenuItem>
  );
}

/**
 * Renders via Vapor's low-level MenuItem primitive (registers as a real,
 * keyboard-reachable menuitem descendant) instead of MenuButton, because
 * MenuButton hardcodes truncate=true on its primary label with no prop to
 * override it — this row needs the project name to NOT truncate, matching
 * the legacy in-menu header's behavior. Reusing ProjectHeaderContent directly
 * (the same component ProjectHeaderMenuTrigger uses) also means avatar/text
 * spacing matches the trigger by construction, with no CSS pixel-matching
 * needed. MenuItem's `bind` intentionally has no visual state we react to,
 * so this row never shows the hover/selected highlight other menu items get
 * (a deliberate product decision — see A11Y-2126).
 */
const Container = styled.div`
  align-items: start;
  cursor: pointer;
  display: flex;
  outline: none;
  padding-top: ${theme.spaces[8]};
  width: 100%;
`;
